// Copyright (c) Docugami, Inc. All rights reserved.

import { TooltipHost, TooltipDelay } from "@fluentui/react";
import { DisabledCss } from "components/common/DgCommonStyledComponents";
import { ControlPanelActionButtonLabel } from "components/common/Fonts";
import { appContentColor, chunkToolbarColor, hightlightBlue, homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { IconRenderer, IconRendererProps } from "components/work_path/IconRenderer"
import styled from 'styled-components';

const ItemContainer = styled.div<{ height?: string }>`
    display: flex;
    flex-direction: column;
    width: ${props => props.height ?? '35px'};
    height: ${props => props.height ?? '35px'};
    align-items: center;
    justify-content: center;
`;

const Root = styled.div<{ selected?: boolean, disabled?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    cursor: pointer;
    background-color: ${props => !props.selected ? primaryTheme.palette.white : chunkToolbarColor};
    color: ${props => !!props.selected ? primaryTheme.palette.white : appContentColor};
    ${props => !!props.disabled && DisabledCss}
    :hover {
        ${props => !props.disabled && !props.selected && `background-color: ${hightlightBlue}`};
        ${ItemContainer} {
            transform: scale(${props => !!props.disabled ? 1 : 1.05});
        }
        :active {
            ${props => !props.disabled && `background-color: ${chunkToolbarColor}`};
            ${ItemContainer} {
                transform: scale(1);
            }
        }
    }
`;

const LabelText = styled(ControlPanelActionButtonLabel)`
    font-size: 12px;
    padding-right: 10px;
    align-self: center;
`;

export interface DocHeaderButtonProps {
    iconProps: IconRendererProps;
    isSelected: boolean;
    onClick: () => void;
    disabled?: boolean;
    onHover?: (isHovered: boolean) => void;
    label?: string;
    showLabel?: boolean;
    height?: string;
}

export const DocumentHeaderButton = (props: DocHeaderButtonProps): JSX.Element => {
    return (
        <TooltipHost
            content={!!props.showLabel ? '' : props.label}
            delay={TooltipDelay.long}>
            <Root selected={props.isSelected} disabled={props.disabled} onClick={() => {
                if (!props.disabled) {
                    props.onClick()
                }
            }}>
                <ItemContainer height={props.height}>
                    <IconRenderer
                        {...{
                            ...props.iconProps,
                            source: {
                                ...props.iconProps.source,
                                defaultColor: props.isSelected ? primaryTheme.palette.white : homeGray
                            }
                        }} />
                </ItemContainer>
                {!!props.showLabel ? <LabelText>{props.label?.toLocaleUpperCase()}</LabelText> : <></>}
            </Root>
        </TooltipHost>
    );
}
